import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { dropPlace, MAX_PAGE_NAME, MAX_PAGES } from '../editor/pageFormat';
import { $, $in } from './dom';
import { pageThumb, readyThumb } from './pageThumbs';
import { openSheet } from './sheets';
import { toast } from './toast';

/*
 * Pages along the bottom of the work area (แถบหน้า, choices 1ก 2ก 3ก 5ก):
 *   strip          a preview of every page with its number and name; tap = open it, tap the open one = its menu;
 *                  "+" between pages = a blank page there (the size of the page before it); press and hold = drag to
 *                  another place (let go without moving = the page's menu); folds away to "‹ หน้า 2 / 5 ›" (remembered)
 *   page menu      ตั้งชื่อ · ทำซ้ำ · เพิ่มหน้าว่างถัดไป · ย้าย · ลบ (tap again to delete)
 *   page toolbar   the same for the open page, plus ขนาดหน้า and สีพื้นหลัง, in the bar above the work (tablet) while nothing
 *                  is chosen — that row is there anyway (BUG-38), so nothing moves under the finger
 * Previews are drawn only for pages that can be seen, one at a time, and kept (src/ui/pageThumbs.ts); the strip is
 * redrawn only when the pages change, never under a finger. Every refusal says why.
 */

const FOLD_KEY = 'craftstudio-page-strip-folded';
const THUMB_H = 56, DRAW_SIDE = 200, LONG_MS = 420, MOVE_PX = 8;
/** room kept free under the artboard (src/editor/editor.ts setInsetBottom): strip + chips, or the small switcher */
const INSET_OPEN = 160, INSET_FOLDED = 96;
const READ_ONLY = 'งานนี้เป็นของคนอื่น เปิดดูได้อย่างเดียว';

const svg = (d: string) => `<svg viewBox="0 0 24 24">${d}</svg>`;
const I = {
  name: svg('<path d="M4 20h4L19 9l-4-4L4 16z"/>'),
  dup: svg('<rect x="8" y="8" width="12" height="12" rx="2"/><path d="M14 11v6M11 14h6M16 8V5a1 1 0 00-1-1H5a1 1 0 00-1 1v10a1 1 0 001 1h3"/>'),
  add: svg('<rect x="5" y="3" width="14" height="18" rx="2"/><path d="M12 9v6M9 12h6"/>'),
  left: svg('<path d="M15 6l-6 6 6 6"/>'),
  right: svg('<path d="M9 6l6 6-6 6"/>'),
  size: svg('<rect x="4" y="4" width="16" height="16" rx="1"/><path d="M4 9h3M9 4v3"/>'),
  bg: svg('<path d="M12 3a9 9 0 100 18c1.1 0 1.5-.8 1.5-1.6 0-.9-.6-1.4-.6-2.2 0-1 .8-1.7 1.8-1.7H17a4 4 0 004-4c0-4.7-4-8.5-9-8.5z"/>'),
  del: svg('<path d="M5 7h14M10 11v6M14 11v6M7 7l1 12h8l1-12M9 7V4h6v3"/>')
};

interface PageAct { label: string; icon: string; run: () => string | null; off?: string | null; danger?: boolean }

const remembered = () => { try { return localStorage.getItem(FOLD_KEY) === '1'; } catch { return false; } };
const remember = (on: boolean) => { try { localStorage.setItem(FOLD_KEY, on ? '1' : '0'); } catch { /* not remembered, still works */ } };

export function initPageStrip(ed: Editor, history: History, opts: { canEdit: () => boolean }) {
  const strip = $('pStrip'), row = $('psRow'), menu = $('psMenu'), tools = $('pageTools'), nav = $('pageNav');
  let folded = remembered();
  /** the page whose delete is waiting for its second tap */
  let armed: number | null = null;
  let armTimer: ReturnType<typeof setTimeout> | undefined;
  /** what the strip was last drawn for (pages, sizes, names, open page): redrawn only when it changes */
  let drawnFor = '';
  /** a finger/pointer is down on the strip: no redraw until it lifts (the button under it would be replaced) */
  let pressing = false, redrawLater = false;

  /* ---------- what can be done to a page ---------- */

  function disarm() { armed = null; clearTimeout(armTimer); }
  const pageLabel = (k: number) => { const n = history.pageName(k); return `หน้า ${k + 1}${n ? ' · ' + n : ''}`; };

  function acts(k: number): PageAct[] {
    const n = history.pageCount(), ro = opts.canEdit() ? null : READ_ONLY;
    const full = n >= MAX_PAGES ? `งานหนึ่งมีได้สูงสุด ${MAX_PAGES} หน้า` : null;
    return [
      { label: history.pageName(k) ? 'เปลี่ยนชื่อหน้า' : 'ตั้งชื่อหน้า', icon: I.name, off: ro, run: () => { openName(k); return null; } },
      { label: 'ทำซ้ำหน้า', icon: I.dup, off: ro ?? full, run: () => done(history.addPage(k, k), `ทำซ้ำหน้า ${k + 1} เป็นหน้า ${k + 2} แล้ว`) },
      { label: 'เพิ่มหน้าว่างถัดไป', icon: I.add, off: ro ?? full, run: () => addAfter(k) },
      { label: 'ย้ายไปก่อนหน้า', icon: I.left, off: ro ?? (k === 0 ? 'หน้านี้อยู่หน้าแรกแล้ว' : null), run: () => done(history.movePage(k, -1), `ย้ายเป็นหน้า ${k} แล้ว`) },
      { label: 'ย้ายไปถัดไป', icon: I.right, off: ro ?? (k === n - 1 ? 'หน้านี้อยู่หน้าสุดท้ายแล้ว' : null), run: () => done(history.movePage(k, 1), `ย้ายเป็นหน้า ${k + 2} แล้ว`) },
      {
        label: armed === k ? `แตะอีกครั้งเพื่อลบหน้า ${k + 1}` : 'ลบหน้า', icon: I.del, danger: true,
        off: ro ?? (n <= 1 ? 'งานต้องมีอย่างน้อย 1 หน้า ใช้ "เริ่มงานใหม่" ในเมนูเพิ่มเติมถ้าต้องการล้างหน้างาน' : null),
        run: () => {
          if (armed !== k) { disarm(); armed = k; armTimer = setTimeout(() => { disarm(); paintTools(); if (!menu.hidden) paintMenu(); }, 3500); return null; }
          disarm();
          return done(history.deletePage(k), `ลบหน้า ${k + 1} แล้ว กดเลิกทำถ้าต้องการกลับไป`);
        }
      }
    ];
  }
  /** a page action's result: the reason when refused, else the message */
  function done(why: string | null, ok: string): string | null { if (!why) toast(ok); return why; }
  /** a blank page after page `k`, the size and bleed of page `k` */
  function addAfter(k: number): string | null {
    if (!opts.canEdit()) return READ_ONLY;
    if (history.pageCount() >= MAX_PAGES) return `งานหนึ่งมีได้สูงสุด ${MAX_PAGES} หน้า`;
    return done(history.addPage(k, undefined, history.pageSize(k)), `เพิ่มหน้าว่างเป็นหน้า ${k + 2} แล้ว`);
  }
  const say = (why: string | null) => { if (why) toast(why, true); };

  /* ---------- the strip ---------- */

  const signature = () => {
    const n = history.pageCount(), sizes = history.pageSizes();
    return [n, history.currentPage(), opts.canEdit(), ...sizes.map((s, k) => `${s.w}x${s.h}:${history.pageName(k)}`)].join('|');
  };

  const seen = new IntersectionObserver(entries => {
    // while a page loads, leave it watched: the strip is drawn again (and watched afresh) once the page is open
    if (history.loading) return;
    for (const e of entries) if (e.isIntersecting) { seen.unobserve(e.target); drawThumb(e.target as HTMLElement); }
  }, { root: row, rootMargin: '0px 240px' });

  /** draw (or reuse) the preview of the page in this item */
  function drawThumb(item: HTMLElement) {
    const k = Number(item.dataset.k);
    if (!(k < history.pageCount()) || history.loading) return;
    const json = history.pageJson(k), size = history.pageSize(k);
    const pic = item.querySelector<HTMLElement>('.psimg')!;
    const ready = readyThumb(json, size, DRAW_SIDE);
    if (ready) { setPic(pic, ready); return; }
    pageThumb(json, size, ed.doc, DRAW_SIDE).then(
      url => { if (pic.isConnected) setPic(pic, url); },
      e => { if (pic.isConnected) { pic.textContent = '!'; pic.title = 'แสดงตัวอย่างไม่ได้: ' + (e instanceof Error ? e.message : String(e)); } }
    );
  }
  function setPic(pic: HTMLElement, url: string) { pic.style.backgroundImage = `url("${url}")`; pic.textContent = ''; pic.title = ''; }

  function paintStrip() {
    if (pressing) { redrawLater = true; return; }
    const sig = signature();
    if (sig === drawnFor) return;
    const pageChanged = drawnFor.split('|')[1] !== String(history.currentPage());
    drawnFor = sig;
    closeMenu();
    seen.disconnect();   // the old previews are gone
    const n = history.pageCount(), cur = history.currentPage(), sizes = history.pageSizes();
    row.textContent = '';
    for (let k = 0; k < n; k++) {
      const item = document.createElement('div');
      item.className = 'psitem'; item.dataset.k = String(k); item.setAttribute('role', 'listitem');
      const b = document.createElement('button');
      b.className = 'psthumb';
      b.setAttribute('aria-current', String(k === cur));
      b.setAttribute('aria-label', `${pageLabel(k)}${k === cur ? ' (เปิดอยู่ แตะเพื่อดูเมนูของหน้า)' : ''}`);
      const ratio = sizes[k].w / sizes[k].h;
      const pic = document.createElement('span');
      pic.className = 'psimg';
      pic.style.width = `${Math.round(Math.min(112, Math.max(32, THUMB_H * ratio)))}px`;
      const num = Object.assign(document.createElement('span'), { className: 'psnum', textContent: String(k + 1) });
      b.append(pic, num);
      const name = history.pageName(k);
      const cap = Object.assign(document.createElement('span'), { className: 'psname', textContent: name || `หน้า ${k + 1}` });
      if (!name) cap.classList.add('none');
      item.append(b, cap);
      row.append(item);
      row.append(plus(k));
      seen.observe(item);
    }
    if (pageChanged) row.querySelector('[aria-current="true"]')?.parentElement?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }

  /** "+" after page `k`: a blank page there */
  function plus(k: number) {
    const last = k === history.pageCount() - 1;
    const p = document.createElement('button');
    p.className = 'psplus';
    p.textContent = '+';
    p.setAttribute('aria-label', last ? 'เพิ่มหน้าว่างต่อท้าย' : `เพิ่มหน้าว่างระหว่างหน้า ${k + 1} และ ${k + 2}`);
    p.onclick = () => say(addAfter(k));
    return p;
  }

  /** the open page's preview, after an edit (a moment after, so typing is not slowed) */
  let editTimer: ReturnType<typeof setTimeout> | undefined;
  function changed() {
    clearTimeout(editTimer);
    editTimer = setTimeout(() => {
      paintStrip();
      paintTools();
      const item = row.querySelector<HTMLElement>(`.psitem[data-k="${history.currentPage()}"]`);
      if (item && !folded) drawThumb(item);
    }, 1200);
  }

  /* ---------- tap, press and hold, drag ---------- */

  let press: { k: number; x: number; y: number; id: number; item: HTMLElement; timer: ReturnType<typeof setTimeout>; lifted: boolean; moved: boolean; to: number; centres: number[] } | null = null;
  let scrollRun = 0;

  row.addEventListener('pointerdown', e => {
    const b = (e.target as Element).closest('.psthumb');
    if (!b || (e.pointerType === 'mouse' && e.button !== 0)) return;
    const item = b.parentElement as HTMLElement, k = Number(item.dataset.k);
    pressing = true;
    press = {
      k, x: e.clientX, y: e.clientY, id: e.pointerId, item, lifted: false, moved: false, to: k, centres: [],
      timer: setTimeout(() => {
        if (!press) return;
        press.lifted = true;
        // someone else's design: holding opens the page's menu (greyed, with the reason) — the page is not picked up
        if (!opts.canEdit()) return;
        // the other pages' centres, measured once before anything moves (the held one moves with the finger — BUG-49)
        const others = [...row.querySelectorAll<HTMLElement>('.psitem')].filter(it => it !== item);
        press.centres = others.map(it => { const r = it.getBoundingClientRect(); return r.left + r.width / 2 + row.scrollLeft; });
        item.classList.add('lift');
        try { row.setPointerCapture(press.id); } catch { /* moves still arrive while over the strip */ }
      }, LONG_MS)
    };
  });
  // while a page is held the strip must not scroll under the finger (pan-x would take the touch away)
  row.addEventListener('touchmove', e => { if (press?.lifted) e.preventDefault(); }, { passive: false });
  row.addEventListener('pointermove', e => {
    if (!press || e.pointerId !== press.id) return;
    const dx = e.clientX - press.x, dy = e.clientY - press.y;
    if (!press.lifted) {
      if (Math.hypot(dx, dy) > MOVE_PX) { clearTimeout(press.timer); press = null; release(); }   // scrolling the strip
      return;
    }
    if (!press.centres.length && history.pageCount() > 1) return;   // read-only: not picked up
    if (!press.moved && Math.abs(dx) < MOVE_PX) return;
    press.moved = true;
    press.item.style.transform = `translateX(${dx}px)`;
    press.to = dropPlace(press.centres, e.clientX + row.scrollLeft);
    markDrop(press.to, press.k);
    autoScroll(e.clientX);
  });
  const end = (e: PointerEvent, cancel: boolean) => {
    if (!press || e.pointerId !== press.id) return;
    const p = press; press = null;
    clearTimeout(p.timer);
    p.item.classList.remove('lift'); p.item.style.transform = '';
    markDrop(-1, -1); scrollRun++;
    release();
    if (cancel) return;
    if (p.lifted && p.moved) {
      if (p.to === p.k) { toast(`วางหน้า ${p.k + 1} ที่เดิม ไม่ได้ย้าย (ลากไปวางระหว่างหน้าอื่นเพื่อย้าย)`); return; }
      say(done(opts.canEdit() ? history.movePageTo(p.k, p.to) : READ_ONLY, `ย้ายหน้า ${p.k + 1} ไปเป็นหน้า ${p.to + 1} แล้ว`));
      return;
    }
    if (p.lifted || p.k === history.currentPage()) { openMenu(p.k, p.item); return; }
    disarm();
    say(history.goPage(p.k));
  };
  row.addEventListener('pointerup', e => end(e, false));
  row.addEventListener('pointercancel', e => end(e, true));
  // keyboard (Enter/Space on a preview): open that page, or the menu of the open one
  row.addEventListener('click', e => {
    if ((e as MouseEvent).detail !== 0) return;
    const b = (e.target as Element).closest('.psthumb'); if (!b) return;
    const item = b.parentElement as HTMLElement, k = Number(item.dataset.k);
    if (k === history.currentPage()) openMenu(k, item); else say(history.goPage(k));
  });
  function release() { pressing = false; if (redrawLater) { redrawLater = false; paintStrip(); } }

  function markDrop(to: number, from: number) {
    row.querySelectorAll('.psitem').forEach((it, k) => {
      it.classList.toggle('dropbefore', to >= 0 && k === to && to < from);
      it.classList.toggle('dropafter', to >= 0 && k === to && to > from);
    });
  }
  /** near the strip's ends, scroll it while a page is held there */
  function autoScroll(x: number) {
    const r = row.getBoundingClientRect(), edge = 48;
    const speed = x < r.left + edge ? -12 : x > r.right - edge ? 12 : 0;
    const run = ++scrollRun;
    if (!speed) return;
    const step = () => { if (run !== scrollRun || !press) return; row.scrollLeft += speed; requestAnimationFrame(step); };
    requestAnimationFrame(step);
  }

  /* ---------- the page menu ---------- */

  let menuFor = -1;
  function openMenu(k: number, item: HTMLElement) {
    menuFor = k;
    paintMenu();
    menu.hidden = false;
    const stage = menu.parentElement as HTMLElement, sr = stage.getBoundingClientRect(), ir = item.getBoundingClientRect();
    const w = menu.offsetWidth, h = menu.offsetHeight;
    menu.style.left = `${Math.round(Math.min(Math.max(8, ir.left - sr.left + ir.width / 2 - w / 2), stage.clientWidth - w - 8))}px`;
    menu.style.top = `${Math.round(Math.max(8, ir.top - sr.top - h - 8))}px`;
  }
  function paintMenu() {
    const k = menuFor;
    menu.textContent = '';
    const head = document.createElement('div');
    head.className = 'ocitem text'; head.innerHTML = '<i></i><span></span>';
    head.querySelector('span')!.textContent = pageLabel(k);
    menu.append(head);
    for (const a of acts(k)) menu.append(button(a, 'ocitem', () => { if (menuFor === k && armed === k) paintMenu(); else closeMenu(); }));
  }
  function closeMenu() { if (menu.hidden) return; menu.hidden = true; menu.textContent = ''; menuFor = -1; }
  document.addEventListener('pointerdown', e => { if (!menu.hidden && !menu.contains(e.target as Node)) { closeMenu(); disarm(); paintTools(); } }, true);
  document.addEventListener('keydown', e => { if (e.key === 'Escape') { closeMenu(); closeName(); } });

  /** a button for a page action: greyed ones say why; a refusal is shown; `after` runs once it has run */
  function button(a: PageAct, cls: string, after: () => void) {
    const b = document.createElement('button');
    b.className = cls + (a.danger ? ' danger' : '') + (a.off ? ' off' : '');
    if (a.off) b.setAttribute('aria-disabled', 'true');
    b.setAttribute('role', cls === 'ocitem' ? 'menuitem' : 'button');
    b.innerHTML = cls === 'ocitem' ? `<i>${a.icon}</i><span></span>` : `${a.icon}<span></span>`;
    b.querySelector('span')!.textContent = a.label;
    b.setAttribute('aria-label', a.label);
    b.onclick = () => {
      if (a.off) { toast(a.off, true); return; }
      say(a.run());
      after();
    };
    return b;
  }

  /* ---------- page toolbar (the open page; tablet, nothing chosen) ---------- */

  function paintTools() {
    const k = history.currentPage();
    tools.textContent = '';
    const head = Object.assign(document.createElement('b'), { className: 'ptlabel', textContent: pageLabel(k) });
    tools.append(head);
    const list = acts(k);
    const extra: PageAct[] = [
      { label: 'ขนาดหน้า', icon: I.size, off: opts.canEdit() ? null : READ_ONLY, run: () => { openSheet('shMore'); return null; } },
      { label: 'สีพื้นหลัง', icon: I.bg, off: opts.canEdit() ? null : READ_ONLY, run: () => { openSheet('shMore'); $('dBg').scrollIntoView({ block: 'nearest' }); return null; } }
    ];
    // name · duplicate · add · ‹ › · size · background · delete
    for (const a of [...list.slice(0, 5), ...extra, list[5]]) tools.append(button(a, 'tchip ptchip', () => paintTools()));
  }

  /* ---------- the page name box ---------- */

  const box = $('pnBox'), input = $in('pnIn'), msg = $('pnMsg');
  let naming = -1;
  function openName(k: number) {
    closeMenu();
    naming = k;
    $('pnLbl').textContent = `ชื่อหน้า ${k + 1}`;
    input.value = history.pageName(k);
    msg.textContent = `ไม่เกิน ${MAX_PAGE_NAME} ตัวอักษร · เว้นว่างเพื่อลบชื่อ · ใช้เป็นชื่อไฟล์ตอนส่งออกทีละหน้า`;
    msg.classList.remove('bad');
    box.hidden = false;
    input.focus(); input.select();
  }
  function closeName() { box.hidden = true; naming = -1; }
  function saveName() {
    if (naming < 0) return;
    const k = naming, why = history.setPageName(k, input.value);
    if (why) { msg.textContent = why; msg.classList.add('bad'); return; }
    closeName();
    toast(history.pageName(k) ? `ตั้งชื่อหน้า ${k + 1} เป็น "${history.pageName(k)}" แล้ว` : `ลบชื่อหน้า ${k + 1} แล้ว`);
    drawnFor = ''; paintStrip(); paintTools();
  }
  $('pnSave').onclick = saveName;
  $('pnCancel').onclick = closeName;
  input.addEventListener('keydown', e => { if (e.key === 'Enter' && !e.isComposing) { e.preventDefault(); saveName(); } });
  input.addEventListener('input', () => {
    const n = [...input.value.trim()].length;
    msg.classList.toggle('bad', n > MAX_PAGE_NAME);
    msg.textContent = n > MAX_PAGE_NAME ? `ยาวเกิน ${MAX_PAGE_NAME} ตัวอักษร (ตอนนี้ ${n})` : `${n} / ${MAX_PAGE_NAME} ตัวอักษร`;
  });

  /* ---------- fold ---------- */

  function applyFold() {
    strip.hidden = folded;
    nav.hidden = !folded;
    document.querySelector('.app')?.classList.toggle('strip-open', !folded);
    ed.setInsetBottom(folded ? INSET_FOLDED : INSET_OPEN);
    if (!folded) { drawnFor = ''; paintStrip(); }
  }
  $('psFold').onclick = () => { folded = true; remember(true); closeMenu(); applyFold(); };
  $('pgUnfold').onclick = () => { folded = false; remember(false); applyFold(); };
  applyFold();
  // drawn once now: a design that opens without any page change (a new blank one) would show an empty bar (BUG-50)
  paintTools();

  /** the pages changed (opened, added, moved, deleted, renamed, undone) */
  function sync() {
    if (!folded) paintStrip();
    paintTools();
    // the pages changed under the name box (moved, deleted, undone): it would name whichever page has that number now
    closeName();
  }

  return { sync, changed };
}
